import React, { useState } from 'react';
import SanitizedHtml from 'src/components/ui/SanitizedHtml';
import Button from '@/components/ui/Button';
import { XIcon } from '@phosphor-icons/react';
import { Notification, NotificationAction, NotificationActionParam } from '../types';
import { apiPost, apiPut, apiDelete, apiGet } from 'src/lib/api/axios';
import { usePostHog } from 'src/lib/posthog';

interface NotificationModalProps {
  notification: Notification;
  onClose: () => void;
  onAcknowledgement: () => void;
  onDismiss: () => void;
  onNeverShow: () => void;
}

/**
 * Converts action params to a request body object.
 * Handles type conversion based on param_type.
 */
function buildRequestBody(params?: NotificationActionParam[]): Record<string, unknown> | undefined {
  if (!params || params.length === 0) return undefined;

  const body: Record<string, unknown> = {};
  for (const param of params) {
    switch (param.param_type) {
      case 'number':
        body[param.key] = Number(param.value);
        break;
      case 'boolean':
        body[param.key] = param.value === 'true';
        break;
      default:
        body[param.key] = param.value;
    }
  }
  return body;
}

/**
 * Executes an API call based on action configuration.
 */
async function executeApiCall(action: NotificationAction): Promise<void> {
  const { api_endpoint, api_method, params } = action;

  if (!api_endpoint) {
    throw new Error('API endpoint is required for api_call action');
  }

  const body = buildRequestBody(params);
  const method = (api_method || 'POST').toUpperCase();

  switch (method) {
    case 'GET':
      await apiGet(api_endpoint);
      break;
    case 'POST':
      await apiPost(api_endpoint, body || {});
      break;
    case 'PUT':
      await apiPut(api_endpoint, body || {});
      break;
    case 'DELETE':
      await apiDelete(api_endpoint);
      break;
    default:
      throw new Error(`Unsupported HTTP method: ${method}`);
  }
}

export const NotificationModal: React.FC<NotificationModalProps> = ({
  notification,
  onClose,
  onAcknowledgement,
  onDismiss,
  onNeverShow
}) => {
  const posthog = usePostHog();
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  const handleAction = async (action: NotificationAction) => {
    setIsProcessingAction(true);
    try {
      // Track notification action in PostHog
      posthog?.capture('notification_action_triggered', {
        notificationId: notification.id,
        notificationTitle: notification.title,
        actionType: action.action_type,
        actionLabel: action.label,
        apiEndpoint: action.api_endpoint,
        apiMethod: action.api_method,
        navigateUrl: action.navigate_url
      });

      switch (action.action_type) {
        case 'api_call':
          // Execute the configured API call
          await executeApiCall(action);
          // Acknowledge the notification after successful API call
          onAcknowledgement();
          break;

        case 'navigate':
          // Navigate to the configured URL
          if (action.navigate_url) {
            window.location.href = action.navigate_url;
          }
          onAcknowledgement();
          break;

        case 'dismiss':
          // Dismiss the notification (temporary)
          onDismiss();
          break;

        case 'close':
          // Just close without tracking
          onClose();
          break;

        default:
          // Unknown action type, just acknowledge
          console.warn(`Unknown action type: ${action.action_type}`);
          onAcknowledgement();
      }
    } catch (error) {
      console.error('Failed to execute notification action:', error);
      // Still close the notification even if action fails
      onClose();
    } finally {
      setIsProcessingAction(false);
    }
  };

  // Sort actions by display_order
  const sortedActions = notification.actions
    ? [...notification.actions].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
    : [];

  return (
    <div className="fixed inset-0 z-[999] bg-black/50 flex justify-center items-center p-4">
      <div className="bg-white p-6 rounded-2xl shadow-xl max-w-lg w-full relative">
        {/* Close X button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-gray-600 hover:bg-gray-200 rounded-full p-1 transition-colors"
          aria-label="Close"
        >
          <XIcon size={24} />
        </button>

        {/* Title */}
        <h3 className="text-lg font-medium text-content-default mb-4 pr-8">{notification.title}</h3>

        {/* Content with HTML rendering */}
        <SanitizedHtml className="text-content-default mb-6 prose max-w-none" html={notification.content} />

        {/* Actions */}
        <div className="flex justify-end gap-3">
          {sortedActions.length > 0 ? (
            <>
              {sortedActions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant === 'primary' ? 'primary' : 'outline'}
                  size="md"
                  onClick={() => handleAction(action)}
                  disabled={isProcessingAction}
                >
                  {action.label}
                </Button>
              ))}
            </>
          ) : (
            <Button variant="primary" size="md" onClick={onAcknowledgement}>
              Okay, got it
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
